<template>
  <div class="min-h-screen">
    <div class="flex flex-col h-screen overflow-hidden bg-growth-25">
      <div
        v-if="environmentBannerLabel"
        :class="environmentBannerClasses"
        class="flex shrink-0 items-center justify-center px-4 py-1.5 text-center text-xs font-bold tracking-wide"
        role="status"
      >
        {{ environmentBannerLabel }}
      </div>
      <Top_Nav_Bar v-if="hasAppChrome" />

      <!-- Content area with sidebar -->
      <div :class="['flex flex-col md:flex-row flex-1 overflow-hidden', isAuthenticated && hasAppChrome ? 'border-t border-gray-200' : '']">
        <!-- Main App Navigation Sidebar (always visible) -->
        <MainNavSidebar v-if="isAuthenticated && hasAppChrome" />

        <!-- Main content area. .fic-capture-target marks the region the
             Focus Item capture widget screenshots (content only, not chrome). -->
        <div class="flex-1 overflow-auto bg-white h-full fic-capture-target">
          <router-view />
        </div>
      </div>
    </div>

    <!-- App-wide Focus Item capture overlay (admins only; self-gated). -->
    <FocusItemCapture v-if="isAuthenticated && !isDemoMode" />

    <AnnouncementModal v-if="hasAppChrome && !isDemoMode" ref="announcementModal" />
    <AnnouncementEditor v-if="current_user?.is_superuser && hasAppChrome && !isDemoMode" />

    <!-- Persistent floating phone call UI — only visible during active calls -->
    <PhoneCallFloater v-if="isAuthenticated" />

    <!-- Agent slide-over: the one agent on any page; breaks out to /agent/<session>. -->
    <AgentOverlay v-if="isAuthenticated && hasAppChrome" />

    <!-- Demo Mode presenter controls: status indicator (preparing / on /
         failed), privacy cover and exit. Rendered for every layout. -->
    <DemoModePresenter v-if="isDemoMode" />
  </div>
</template>

<script>
import Top_Nav_Bar from '@/components/navigation/Top_Nav_Bar.vue';
import MainNavSidebar from '@/components/navigation/MainNavSidebar.vue';
import AnnouncementModal from '@/components/AnnouncementModal.vue';
import AnnouncementEditor from '@/components/AnnouncementEditor.vue';
import FocusItemCapture from '@/components/FocusItemCapture.vue';
import PhoneCallFloater from '@/components/PhoneCallFloater.vue';
import AgentOverlay from '@/components/Beta/AgentOverlay.vue';
import DemoModePresenter from '@/components/DemoMode/DemoModePresenter.vue';
import { mapGetters } from 'vuex';
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';
import {
  bootPylonWidget,
  shutdownPylonWidget,
  hidePylonWidget,
  suppressPylonForDemo,
} from '@/services/pylonWidget';
import { twilioDevice } from '@/services/TwilioDeviceService';
import { environmentBannerClasses, environmentBannerLabel } from '@/utils/environment';

export default {
  name: 'App',
  components: {
    FocusItemCapture,
    PhoneCallFloater,
    AgentOverlay,
    Top_Nav_Bar,
    MainNavSidebar,
    AnnouncementModal,
    AnnouncementEditor,
    DemoModePresenter,
  },
  data() {
    return {
      pylonBooted: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user', 'isAuthenticated']),
    ...mapGetters('tenant', ['current_tenant_slug', 'current_tenant_name']),
    // Demo Mode state is owned by the demo controller (utils/demoController.js);
    // $root.isDemoMode remains the template-facing alias.
    ...mapGetters('demoMode', ['isDemoMode']),
    currentLayout() {
      return this.$route.meta.layout
    },
    hasAppChrome() {
      return !['auth', 'bare', 'external'].includes(this.currentLayout);
    },
    environmentBannerLabel() {
      return environmentBannerLabel();
    },
    environmentBannerClasses() {
      return environmentBannerClasses();
    },
    pylonEntitledUser() {
      if (this.current_user && canAccessFeature(FEATURES.WIDGET_PYLON)) {
        return this.current_user;
      }
      return null;
    },
  },
  watch: {
    isDemoMode: {
      immediate: true,
      handler(val) {
        // Close an already-open support chat as well as its launcher: a real
        // support thread must not stay on screen once Demo Mode starts.
        if (val) suppressPylonForDemo();
        else this.syncPylonExperience();
      },
    },

    pylonEntitledUser: {
      immediate: true,
      async handler(user) {
        if (!user) {
          if (this.pylonBooted) shutdownPylonWidget();
          this.pylonBooted = false;
          return;
        }

        const bootUser = user;
        await bootPylonWidget(bootUser);

        // The flag, tenant, or user can change while identity verification is
        // in flight. Do not expose a widget that is no longer authorized.
        if (this.pylonEntitledUser !== bootUser) {
          shutdownPylonWidget();
          return;
        }

        this.pylonBooted = true;
        document.body.classList.add('integrated-pylon-widget');
        this.syncPylonExperience();
      },
    },

    current_user(user) {
      if (!user && twilioDevice.isInitialized) {
        twilioDevice.destroy();
      }
      if (user) {
        this.$nextTick(() => {
          this.$refs.announcementModal?.checkModal(this.$route);
        });
      }
    },

    currentLayout() {
      if (!this.pylonBooted || this.isDemoMode) return;
      this.syncPylonExperience();
    },
  },
  mounted() {
    // Handle the case where the user lands directly on a route with a modal
    // (the watcher won't fire for the initial route, only subsequent navigations)
    this.$nextTick(() => {
      this.$refs.announcementModal?.checkModal(this.$route);
    });
  },
  methods: {
    syncPylonExperience() {
      if (!this.pylonBooted || this.isDemoMode) return;
      hidePylonWidget();
    },
  },
};
</script>

<style>
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

/* Growth module design tokens (CSS custom properties) */
:root {
  /* Semantic grays */
  --growth-900: #1E1F21;
  --growth-800: #353537;
  --growth-700: #78797A;
  --growth-600: #626264;
  --growth-50: #E8E9E9;
  --growth-25: #F4F4F4;
  --growth-10: #FAFAFA;

  /* Primary accent */
  --growth-tangerine: #F76734;
  --growth-tangerine-hover: #E55A2B;

  /* Semantic status */
  --growth-info: #7799C2;
  --growth-success: #166534;
  --growth-warning: #92400E;
  --growth-error: #D53D3D;

  /* Shadows */
  --growth-shadow-sm: 0 1px 2px rgba(0,0,0,0.05);
  --growth-shadow-md: 0 4px 12px rgba(0,0,0,0.08);
  --growth-shadow-lg: 0 8px 24px rgba(0,0,0,0.12);

  /* Tag category backgrounds (10-15% opacity) */
  --growth-tag-aco-bg: rgba(100, 116, 139, 0.15);
  --growth-tag-aco-text: #334155;
  --growth-tag-volume-bg: rgba(217, 119, 6, 0.12);
  --growth-tag-volume-text: #92400E;
  --growth-tag-type-bg: rgba(37, 99, 235, 0.10);
  --growth-tag-type-text: #1E40AF;
  --growth-tag-population-bg: rgba(124, 58, 237, 0.10);
  --growth-tag-population-text: #5B21B6;
  --growth-tag-setting-bg: rgba(8, 145, 178, 0.10);
  --growth-tag-setting-text: #0E7490;
  --growth-tag-tech-bg: rgba(107, 114, 128, 0.12);
  --growth-tag-tech-text: #374151;
}

/* Demo mode: prevent selecting/copying sensitive data that hasn't been spoofed yet */
body.demo-mode .sensitive {
  user-select: none;
}

/* Belt-and-suspenders: hide Pylon launcher in demo mode (programmatic hide also applied) */
body.demo-mode #pylon-launcher,
body.demo-mode #pylon-launcher-frame {
  display: none !important;
}

</style>
